import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  MapPin, Camera, CheckCircle, XCircle, Clock, 
  AlertCircle, Trophy, ImagePlus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

// Function to add timestamp to image
const addTimestampToImage = (file) => {
  return new Promise((resolve) => {
    const img = new window.Image();
    const reader = new FileReader();
    
    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        const now = new Date();
        const timestamp = now.toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
        
        const fontSize = Math.max(Math.min(img.width, img.height) * 0.04, 16);
        const padding = fontSize * 0.5;
        
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'bottom';
        
        const textMetrics = ctx.measureText(timestamp);
        const textWidth = textMetrics.width;
        const textHeight = fontSize;
        
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        ctx.fillRect(padding - 5, img.height - textHeight - padding - 10, textWidth + 20, textHeight + 15);
        
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(timestamp, padding, img.height - padding);
        
        const watermark = 'STTGS Overland';
        ctx.font = `${fontSize * 0.6}px Arial`;
        const wmMetrics = ctx.measureText(watermark);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.fillText(watermark, img.width - wmMetrics.width - padding, img.height - padding);
        
        canvas.toBlob((blob) => {
          const timestampedFile = new File([blob], file.name, { type: 'image/jpeg' });
          resolve(timestampedFile);
        }, 'image/jpeg', 0.9);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

export default function WaypointTracker({ registrationId, eventId, canUpload = true }) {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingWaypointId, setUploadingWaypointId] = useState(null);

  useEffect(() => {
    fetchProgress();
  }, [registrationId]);

  const fetchProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/registrations/${registrationId}/waypoint-progress`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching waypoint progress:', error);
      toast.error('Error al cargar progreso de waypoints');
    } finally {
      setLoading(false);
    }
  };

  const handleCameraPhoto = async (waypointId, file) => {
    setUploading(true);
    setUploadingWaypointId(waypointId);
    try {
      toast.info('Añadiendo marca de tiempo...');
      const timestampedFile = await addTimestampToImage(file);
      await uploadPhoto(waypointId, timestampedFile);
    } catch (error) {
      console.error('Error processing camera photo:', error);
      toast.error('Error al procesar la foto');
    } finally {
      setUploading(false);
      setUploadingWaypointId(null);
    }
  };

  const handleGalleryPhoto = async (waypointId, file) => {
    setUploading(true);
    setUploadingWaypointId(waypointId);
    await uploadPhoto(waypointId, file);
    setUploading(false);
    setUploadingWaypointId(null);
  };

  const uploadPhoto = async (waypointId, file) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('waypoint_id', waypointId);
      
      await axios.post(
        `${API}/registrations/${registrationId}/tracking-photos`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('¡Foto subida correctamente!');
      fetchProgress();
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(error.response?.data?.detail || 'Error al subir la foto');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('¿Eliminar esta foto?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/registrations/${registrationId}/tracking-photos/${photoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Foto eliminada');
      fetchProgress();
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Error al eliminar la foto');
    }
  };

  const getStatusIcon = (item) => {
    if (item.is_verified) return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (item.is_completed) return <Clock className="w-4 h-4 text-yellow-500" />;
    if (item.photo?.status === 'rejected') return <XCircle className="w-4 h-4 text-red-500" />;
    return <AlertCircle className="w-4 h-4 text-gray-400" />;
  };

  const getStatusText = (item) => {
    if (item.is_verified) return 'Verificada';
    if (item.photo?.status === 'rejected') return 'Rechazada';
    if (item.is_completed) return 'Pendiente';
    return 'Sin foto';
  };

  const getStatusColor = (item) => {
    if (item.is_verified) return 'bg-green-100 text-green-700';
    if (item.photo?.status === 'rejected') return 'bg-red-100 text-red-700';
    if (item.is_completed) return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-600';
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
        <p className="text-gray-600">Cargando waypoints...</p>
      </div>
    );
  }

  if (!progress || progress.total_waypoints === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-500">Este evento no tiene waypoints definidos</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Progress Summary */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-200">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-orange-500" />
            <span className="font-semibold text-gray-800">Progreso</span>
          </div>
          <span className="text-2xl font-bold text-orange-600">{progress.completion_percentage}%</span>
        </div>
        <Progress value={progress.completion_percentage} className="h-3 mb-2" />
        <p className="text-sm text-gray-600">
          {progress.completed_mandatory} de {progress.total_mandatory} waypoints verificados
        </p>
      </div>

      {/* Waypoints List - Cards with inline buttons */}
      <div className="space-y-4">
        {progress.waypoints.map((item, index) => (
          <div 
            key={item.waypoint.id}
            className={`rounded-xl border-2 overflow-hidden shadow-sm ${
              item.is_verified ? 'bg-green-50 border-green-300' :
              item.photo?.status === 'rejected' ? 'bg-red-50 border-red-300' :
              item.is_completed ? 'bg-yellow-50 border-yellow-300' :
              'bg-white border-gray-200'
            }`}
          >
            {/* Waypoint Header */}
            <div className="p-4 flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl ${
                item.is_verified ? 'bg-green-500 text-white' :
                item.is_completed ? 'bg-yellow-500 text-white' :
                'bg-orange-500 text-white'
              }`}>
                {index + 1}
              </div>

              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg">{item.waypoint.name}</h3>
                {item.waypoint.description && (
                  <p className="text-sm text-gray-600">{item.waypoint.description}</p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  {item.waypoint.is_mandatory && (
                    <Badge className="text-xs bg-red-100 text-red-700">Obligatorio</Badge>
                  )}
                  <Badge className={`text-xs ${getStatusColor(item)}`}>
                    {getStatusIcon(item)}
                    <span className="ml-1">{getStatusText(item)}</span>
                  </Badge>
                </div>
              </div>
            </div>

            {/* Photo Section */}
            <div className="px-4 pb-4">
              {item.photo?.photo_url ? (
                /* User has uploaded a photo */
                <div>
                  <img 
                    src={item.photo.photo_url} 
                    alt={item.waypoint.name}
                    className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                    onClick={() => window.open(item.photo.photo_url, '_blank')}
                  />
                  {item.photo.status === 'rejected' && item.photo.rejection_reason && (
                    <div className="mt-2 p-3 bg-red-100 border border-red-300 rounded-lg">
                      <p className="text-sm text-red-700"><strong>❌ Rechazada:</strong> {item.photo.rejection_reason}</p>
                    </div>
                  )}
                  {canUpload && item.photo.status === 'rejected' && (
                    <Button
                      onClick={() => handleDeletePhoto(item.photo.id)}
                      variant="outline"
                      className="w-full mt-3 h-12 border-red-400 text-red-600 font-semibold"
                    >
                      <Trash2 className="w-5 h-5 mr-2" />
                      Eliminar y subir nueva foto
                    </Button>
                  )}
                </div>
              ) : canUpload ? (
                /* No photo yet - Show BIG upload buttons */
                <div className="bg-gray-50 rounded-xl p-4 border-2 border-dashed border-orange-300">
                  {/* Reference photo */}
                  {item.waypoint.reference_photo_url && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2 font-medium">📸 Foto de referencia:</p>
                      <img 
                        src={item.waypoint.reference_photo_url} 
                        alt="Referencia"
                        className="w-full h-32 object-cover rounded-lg border opacity-80"
                      />
                    </div>
                  )}
                  
                  {/* GPS link */}
                  {item.waypoint.latitude && item.waypoint.longitude && (
                    <a 
                      href={`https://www.google.com/maps?q=${item.waypoint.latitude},${item.waypoint.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 text-blue-600 font-medium mb-4 p-2 bg-blue-50 rounded-lg"
                    >
                      <MapPin className="w-5 h-5" />
                      📍 Ver ubicación en Google Maps
                    </a>
                  )}

                  {/* Hidden file inputs */}
                  <input
                    type="file"
                    id={`camera-${item.waypoint.id}`}
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) handleCameraPhoto(item.waypoint.id, e.target.files[0]);
                      e.target.value = '';
                    }}
                  />
                  <input
                    type="file"
                    id={`gallery-${item.waypoint.id}`}
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) handleGalleryPhoto(item.waypoint.id, e.target.files[0]);
                      e.target.value = '';
                    }}
                  />

                  {/* BIG Upload Buttons */}
                  <div className="space-y-3">
                    <Button 
                      onClick={() => document.getElementById(`camera-${item.waypoint.id}`)?.click()}
                      disabled={uploading}
                      className="w-full h-16 text-xl bg-orange-500 hover:bg-orange-600 font-bold rounded-xl shadow-lg"
                    >
                      {uploading && uploadingWaypointId === item.waypoint.id ? (
                        <>
                          <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin mr-3" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <Camera className="w-7 h-7 mr-3" />
                          📷 HACER FOTO
                        </>
                      )}
                    </Button>
                    
                    <Button 
                      onClick={() => document.getElementById(`gallery-${item.waypoint.id}`)?.click()}
                      disabled={uploading}
                      variant="outline"
                      className="w-full h-14 text-lg border-2 border-orange-400 text-orange-700 hover:bg-orange-100 font-semibold rounded-xl"
                    >
                      <ImagePlus className="w-6 h-6 mr-2" />
                      🖼️ Elegir de Galería
                    </Button>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-4 text-center bg-white p-2 rounded-lg">
                    📷 La cámara añade <strong>fecha y hora</strong> automáticamente
                  </p>
                </div>
              ) : (
                /* Cannot upload */
                <div className="bg-gray-100 rounded-xl p-6 text-center">
                  <Camera className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-500">Foto pendiente</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
